// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import message from "antd/es/message";

/**
 * Shared dedupe window for error toasts coming out of Jotai atoms and
 * `useCurrentUser`. A global outage (e.g. 429) fans out to multiple
 * independent atoms — `folderAtom`, `resourceSlotListAtom`, plus the
 * `CurrentUserErrorToaster` — and without this helper the user sees one
 * toast per path.
 *
 * Scope is intentionally narrow: only the three degrading code paths that
 * would otherwise produce duplicate toasts during a single failure wave
 * should use this. The rest of the app keeps going through
 * `useApiError`/`message.error` directly.
 */
const recentToastKeys = new Map<string, number>();
const DEDUPE_WINDOW_MS = 3000;

/**
 * Show a `message.error(content)` toast unless the same `key` has already
 * been toasted within `DEDUPE_WINDOW_MS`. Keys are short strings like
 * `"currentUser:429"`, `"folder:my-folder"`, `"resourceSlot:load"`.
 */
export function showDedupedErrorToast(key: string, content: string): void {
  const now = Date.now();
  const lastAt = recentToastKeys.get(key);
  if (lastAt != null && now - lastAt < DEDUPE_WINDOW_MS) {
    return;
  }
  recentToastKeys.set(key, now);
  message.error(content);
  // Trivial cleanup so the map doesn't grow unbounded if keys ever become
  // high-cardinality (e.g. per-vFolder).
  setTimeout(() => {
    const stamp = recentToastKeys.get(key);
    if (stamp === now) {
      recentToastKeys.delete(key);
    }
  }, DEDUPE_WINDOW_MS);
}
